import { Ref } from 'vue'
import tokenManager from '../identity/TokenManager'

interface IQueryParam {
  name: string
  value: any
}

type QueryOperation = 'POST' // Por ahora solo se implementa POST

type QueryHeaders = { [key: string]: any }
type QueryBody = { [key: string]: any }

interface IQueryStatus {
  created: Date | null
  started: Date | null
  finished: Date | null
  error: string | null
} // El estado se asume respecto a los valores. Si finished tiene valor, la query terminó. Si started tiene valor y finished es null, está en proceso. Si created tiene valor y started es null, está en cola.

interface IQueryData<T> {
  id: string
  query: string
  operation: QueryOperation
  params?: IQueryParam[]
  headers?: QueryHeaders
  body?: QueryBody
  queryStatus: IQueryStatus
  priority: number // Desde 0 hacia N. Mientras mas alto, mayor prioridad. Por cada envio se aumenta en 1 la prioridad de las queries en cola.
  queryPromise: Promise<void> | null
  resolvers: Array<(value: IQueryResponse<T> | PromiseLike<IQueryResponse<T>>) => void>
}

interface IQueryResponse<T> {
  data: T | null
  status: number
  statusText: string
}

class QueryLauncher {
  private id: string
  public queryList: IQueryData<any>[] = []
  private token: string | null = null

  constructor(id: string) {
    this.id = id
    tokenManager.subscribe(this.updateToken.bind(this))
  }

  updateToken(newToken: string | null) {
    if (newToken) {
      this.token = newToken
    }
  }

  public hasQuery(query: Omit<IQueryData<any>, 'queryPromise'>): number {
    const index = this.queryList.findIndex((q) => {
      return (
        q.query === query.query &&
        q.operation === query.operation &&
        JSON.stringify(q.params) === JSON.stringify(query.params) &&
        JSON.stringify(q.body) === JSON.stringify(query.body)
      )
    })
    return index
  }

  public addQuery<T>(query: Omit<IQueryData<T>, 'queryPromise'>): Promise<IQueryResponse<T>> {
    return new Promise<IQueryResponse<T>>((resolve) => {
      if (this.hasQuery(query) === -1) {
        // Si no existe la query, se lanza una nueva.
        const promise = this.launchQuery({ ...query, resolvers: [resolve] })
        const queryData: IQueryData<T> = { ...query, queryPromise: promise, resolvers: [resolve] }
        this.queryList.push(queryData)
      } else {
        // Si ya existe la query, se retorna la promesa del query existente.
        const existingQuery = this.queryList[this.hasQuery(query)]
        if (existingQuery) {
          existingQuery.resolvers.push(resolve)
        }
      }
    })
  }

  private async launchQuery(query: Omit<IQueryData<any>, 'queryPromise'>) {
    query.queryStatus.started = new Date()
    if (!this.token) {
      await tokenManager.getToken()
    }

    const addHeaders: QueryHeaders = query.headers ? query.headers : {}

    try {
      const response = await fetch(query.query, {
        method: query.operation,
        headers: {
          ...addHeaders,
          Authorization: `Bearer ${this.token}`,
        },
        body: query.body ? JSON.stringify(query.body) : null,
      })

      const data = await response.json()
      query.queryStatus.finished = new Date()
      query.queryStatus.error = null
      if (data && 'Entries' in data && 'Entry' in data.Entries) {
        query.resolvers.forEach((resolver) =>
          resolver({ data: data.Entries.Entry, status: response.status, statusText: response.statusText }),
        )
      } else if (data && 'Entries' in data) {
        query.resolvers.forEach((resolver) =>
          resolver({ data: data.Entries, status: response.status, statusText: response.statusText }),
        )
      } else if (data && 'UpdatedRowCount' in data && 'Value' in data.UpdatedRowCount) {
        query.resolvers.forEach((resolver) =>
          resolver({ data: data.UpdatedRowCount.Value, status: response.status, statusText: response.statusText }),
        )
      } else {
        query.resolvers.forEach((resolver) =>
          resolver({ data: data, status: response.status, statusText: response.statusText }),
        )
      }
    } catch (error: any) {
      query.queryStatus.finished = new Date()
      query.queryStatus.error = error.message
      query.resolvers.forEach((resolver) => resolver({ data: null, status: 500, statusText: error.message }))
    } finally {
      // Eliminar la query de la lista
      const index = this.queryList.findIndex((q) => q.id === query.id)
      if (index > -1) {
        this.queryList.splice(index, 1)
      }
    }
  }
}

class DataQueryManager {
  private QueryHistory: IQueryData<any>[] = []
  private maxQueryLaunchers: number = 5
  private queryLaunchers: QueryLauncher[] = []

  constructor() {}

  startup() {
    console.log('DataQueryManager startup complete')
    this.generateLaunchers()
  }

  private generateLaunchers() {
    for (let i = 0; i < this.maxQueryLaunchers; i++) {
      this.queryLaunchers.push(new QueryLauncher(`launcher-${i}`))
    }
  }

  public async addQuery<T>(QD: {
    query: string
    operation: QueryOperation
    headers: QueryHeaders
    params: Array<IQueryParam>
    body?: QueryBody
  }): Promise<IQueryResponse<T>> {
    const queryData: Omit<IQueryData<T>, 'queryPromise'> = {
      id: this.randomQueryIDGenerator(),
      query: QD.query,
      operation: QD.operation,
      headers: QD.headers,
      params: QD.params,
      body: QD.body,
      queryStatus: { created: new Date(), started: null, finished: null, error: null },
      priority: 0,
      resolvers: [],
    }

    // Buscar un launcher disponible (el que tenga menos queries en cola)
    let selectedLauncher = this.queryLaunchers[0]
    let minQueries = this.queryLaunchers[0].queryList.length

    for (const launcher of this.queryLaunchers) {
      const launcherQueries = launcher.queryList.length
      if (launcherQueries < minQueries) {
        minQueries = launcherQueries
        selectedLauncher = launcher
      }
    }

    const response = selectedLauncher.addQuery<T>(queryData)
    this.QueryHistory.push({ ...queryData, queryPromise: null }) // Se guarda en el historial sin la promesa.
    return response
  }

  private randomQueryIDGenerator(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
  }
}

export function nillToNull(value: string) {
  if (value.includes('@nil')) return null
  if (value === 'null') return null
  return value
}

const dataQueryManager = new DataQueryManager()
export default dataQueryManager
